import { useState } from 'react';
import type { CentroNoResponse, ExcluirCentroResponse } from '@triade/contracts';
import { Link, useNavigate } from '@tanstack/react-router';
import { useArvore, useCentro } from '@/api/centros-custo';
import { usePode } from '@/api/me';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSessao } from '@/lib/session';
import { ArvoreCentros } from './arvore-centros';
import { encontrarNo, todosNos } from './arvore-logica';
import { CentroForm } from './centro-form';
import { MoverCentroDialog } from './mover-centro';
import { ResultadoExclusaoDialog } from './resultado-exclusao';
import { CustoPorMinuto } from './custo-por-minuto';
import type { BuscaCentros } from './tipos';
import { BotaoRelatorios } from '@/relatorios';

/**
 * Tela "Centros de custo" (/custos/centros): aba Centros (arvore sintetico/analitico, com o formulario do centro selecionado ou
 * novo logo abaixo) e aba Custo por minuto (so analiticos). Quem tem `centro.editar` (ADMIN, PCP) edita a estrutura e os minutos;
 * o financeiro edita o CPM na outra aba. Nenhum R$ aparece nesta aba: a API nao manda.
 */
export function CentrosPage({ busca }: { busca: BuscaCentros }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const editavel = pode('centro.editar');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [movendo, setMovendo] = useState<CentroNoResponse | null>(null);
  const [resultadoExclusao, setResultadoExclusao] = useState<{ nome: string; resultado: ExcluirCentroResponse } | null>(null);
  const arvore = useArvore(mostrarInativos);
  const aba = busca.visao === 'cpm' ? 'cpm' : 'centros';

  const mudar = (m: Partial<BuscaCentros>) => void navigate({ to: '/custos/centros', search: (a: BuscaCentros) => ({ ...a, ...m }), replace: true });

  const nos = arvore.data;
  const novo = busca.centro === 'novo';
  const idAberto = busca.centro && !novo ? busca.centro : undefined;
  const naArvore = nos && idAberto ? encontrarNo(nos, idAberto) : undefined;
  // Centro inativo nao vem na arvore sem "Mostrar inativos": busca pelo id para a tela nao ficar sem ele.
  const avulso = useCentro(idAberto && !naArvore ? idAberto : undefined);
  const selecionado = naArvore ?? avulso.data;
  const pai = novo && busca.pai && nos ? encontrarNo(nos, busca.pai) : undefined;
  const formAberto = !!selecionado || novo;

  return (
    <div className="flex w-full flex-col gap-5 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Custos <span aria-hidden="true">/</span>{' '}
            <Link to="/custos/centros" className="text-ink-muted hover:underline">
              Centros de custo
            </Link>
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Centros de custo</h1>
          <p className="mt-1 text-ink-secondary">Onde o custo se forma, os minutos disponíveis e o custo por minuto de cada mês.</p>
        </div>
        <BotaoRelatorios tela="centros" />
      </div>

      {empresaId === null ? (
        <p className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">Escolha a empresa no menu lateral para ver os centros de custo.</p>
      ) : (
        <Tabs value={aba} onValueChange={(v) => mudar(v === 'cpm' ? { visao: 'cpm', centro: undefined, pai: undefined } : { visao: undefined, ate: undefined })}>
          <TabsList aria-label="Seções dos centros de custo">
            <TabsTrigger value="centros">Centros{nos ? ` (${todosNos(nos).length})` : ''}</TabsTrigger>
            <TabsTrigger value="cpm">Custo por minuto</TabsTrigger>
          </TabsList>
          <TabsContent value="centros" className="flex flex-col gap-5">
            <section aria-label="Estrutura de centros" className="rounded-2xl border border-border bg-card">
              <ArvoreCentros
                nos={nos}
                erro={arvore.error ?? undefined}
                onTentar={() => void arvore.refetch()}
                selecionadoId={selecionado?.id}
                mostrarInativos={mostrarInativos}
                onMostrarInativos={setMostrarInativos}
                editavel={editavel}
                onSelecionar={(no) => mudar({ centro: no.id, pai: undefined })}
                onNovoFilho={(no) => mudar({ centro: 'novo', pai: no.id })}
                onNovoRaiz={() => mudar({ centro: 'novo', pai: undefined })}
              />
            </section>

            {formAberto && selecionado && (
              <CentroForm
                key={selecionado.id}
                centro={selecionado}
                filhos={naArvore?.filhos.length ?? 0}
                editavel={editavel}
                onCriado={(c) => mudar({ centro: c.id, pai: undefined })}
                onMover={() => setMovendo(selecionado)}
                onExcluido={(resultado, centro) => {
                  setResultadoExclusao({ nome: centro.nome, resultado });
                  if (resultado.resultado === 'EXCLUIDO') mudar({ centro: undefined, pai: undefined });
                }}
                onNovo={() => mudar({ centro: 'novo', pai: undefined })}
              />
            )}
            {novo && (
              <CentroForm
                key={`novo-${pai?.id ?? 'raiz'}`}
                pai={pai}
                filhos={0}
                editavel={editavel}
                onCriado={(c) => mudar({ centro: c.id, pai: undefined })}
                onNovo={() => mudar({ centro: 'novo', pai: undefined })}
              />
            )}
            {!formAberto && nos && nos.length > 0 && editavel && (
              <p role="status" className="text-small text-ink-muted">
                Escolha um centro na estrutura para editar, ou use "+ raiz" para criar um novo.
              </p>
            )}
            {idAberto && !formAberto && avulso.isError && (
              <p role="status" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
                Esse centro não está na estrutura.
              </p>
            )}
          </TabsContent>
          <TabsContent value="cpm">
            <CustoPorMinuto ate={busca.ate} onAte={(ate) => mudar({ ate })} />
          </TabsContent>
        </Tabs>
      )}

      {movendo && nos && (
        <MoverCentroDialog
          centro={movendo}
          arvore={nos}
          onFechar={() => setMovendo(null)}
          onMovido={(c) => {
            setMovendo(null);
            mudar({ centro: c.id, pai: undefined });
          }}
        />
      )}
      {resultadoExclusao && <ResultadoExclusaoDialog nome={resultadoExclusao.nome} resultado={resultadoExclusao.resultado} onFechar={() => setResultadoExclusao(null)} />}
    </div>
  );
}
